import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  FaBell,
  FaCamera,
  FaChevronRight,
  FaBoxOpen,
  FaCircleNotch,
  FaClock,
  FaCogs,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBullhorn,
} from "react-icons/fa";
import { useNavigate, useSearchParams, Outlet } from "react-router-dom";
import ViewRetailerOrder from "../ViewRetailerOrder/ViewRetailerOrder";
import "./RetailerDashboard.scss";

// --- UTILITIES ---
const normalizeStatus = (s) =>
  s
    ? String(s).trim().charAt(0).toUpperCase() +
      String(s).trim().slice(1).toLowerCase()
    : "Pending";

const extractMasterThumbnail = (order) => {
  if (order.files && order.files.length > 0) return order.files[0];
  if (order.orderType === "Single" && order.singleItems?.length > 0) {
    const item = order.singleItems[0];
    if (item?.fileUrls && item.fileUrls.length > 0) return item.fileUrls[0];
    if (item?.fileUrl) return item.fileUrl;
  }
  if (order.items?.length > 0) {
    const item = order.items[0];
    if (item?.fileUrls && item.fileUrls.length > 0) return item.fileUrls[0];
  }
  return null;
};

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const RetailerDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Active");
  const navigate = useNavigate();

  // URL Query Parameters for Overlay
  const [searchParams] = useSearchParams();
  const viewOrderId = searchParams.get("viewOrder");

  // Mocked Alerts & Notifications
  const [alerts] = useState([
    {
      id: 1,
      type: "urgent",
      title: "Gold Rate Spike",
      message:
        "Gold rates have increased by 2%. Pending quotations may be revised.",
      date: "Today, 10:00 AM",
    },
  ]);
  const [notifications] = useState([
    {
      id: 1,
      title: "Order #4092 Ready",
      message: "Your custom bridal set is ready for pickup.",
      time: "2 hrs ago",
      unread: true,
    },
    {
      id: 2,
      title: "Design Approved",
      message: "Admin approved the CAD design for Order #4088.",
      time: "5 hrs ago",
      unread: true,
    },
  ]);

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : null;
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!session?.id) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const q = query(
          collection(db, "partyOrders"),
          where("partyId", "==", session.id),
        );
        const snap = await getDocs(q);

        let fetchedOrders = snap.docs.map((doc) => {
          const data = doc.data();
          delete data.finalizationData;
          if (data.singleItems)
            data.singleItems.forEach((item) => delete item.finalizationData);
          return { id: doc.id, ...data };
        });

        fetchedOrders.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
        );

        const activeOrders = fetchedOrders.filter(
          (o) => normalizeStatus(o.status) !== "Delivered",
        );
        const deliveredOrders = fetchedOrders
          .filter((o) => normalizeStatus(o.status) === "Delivered")
          .slice(0, 5);

        setOrders([...activeOrders, ...deliveredOrders]);
      } catch (error) {
        console.error("Error fetching retailer orders:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, [session]);

  const stats = useMemo(() => {
    return {
      pending: orders.filter((o) =>
        ["Pending"].includes(normalizeStatus(o.status)),
      ).length,
      production: orders.filter((o) =>
        ["Processing", "Polishing", "Setting"].includes(
          normalizeStatus(o.status),
        ),
      ).length,
      ready: orders.filter((o) =>
        ["Complete", "Hallmarked"].includes(normalizeStatus(o.status)),
      ).length,
    };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const status = normalizeStatus(order.status);
      if (activeTab === "Active")
        return ["Pending", "Processing", "Polishing", "Setting"].includes(
          status,
        );
      if (activeTab === "Completed")
        return ["Complete", "Hallmarked"].includes(status);
      if (activeTab === "Delivered") return status === "Delivered";
      return false;
    });
  }, [orders, activeTab]);

  const getStatusBadge = (status) => {
    const s = normalizeStatus(status);
    if (s === "Pending") return "v-rt-dashboard-badge-warning";
    if (["Processing", "Polishing", "Setting"].includes(s))
      return "v-rt-dashboard-badge-info";
    if (["Complete", "Hallmarked"].includes(s))
      return "v-rt-dashboard-badge-success";
    if (s === "Delivered") return "v-rt-dashboard-badge-neutral";
    return "v-rt-dashboard-badge-default";
  };

  return (
    <div className="v-rt-dashboard-wrapper">
      <header className="v-rt-dashboard-header">
        <div>
          <h1 className="v-rt-dashboard-greeting">
            Welcome back, {session?.partyName || "Partner"}
          </h1>
          <p className="v-rt-dashboard-subtitle">
            Here is your live production overview for today.
          </p>
        </div>
      </header>

      <div className="v-rt-dashboard-kpi-grid">
        <div
          className="v-rt-dashboard-kpi-card"
          onClick={() => setActiveTab("Active")}
        >
          <div className="v-rt-dashboard-kpi-icon pending">
            <FaClock />
          </div>
          <div className="v-rt-dashboard-kpi-data">
            <span className="v-rt-dashboard-kpi-value">{stats.pending}</span>
            <span className="v-rt-dashboard-kpi-label">Pending Review</span>
          </div>
        </div>
        <div
          className="v-rt-dashboard-kpi-card"
          onClick={() => setActiveTab("Active")}
        >
          <div className="v-rt-dashboard-kpi-icon production">
            <FaCogs />
          </div>
          <div className="v-rt-dashboard-kpi-data">
            <span className="v-rt-dashboard-kpi-value">{stats.production}</span>
            <span className="v-rt-dashboard-kpi-label">In Factory</span>
          </div>
        </div>
        <div
          className="v-rt-dashboard-kpi-card"
          onClick={() => setActiveTab("Completed")}
        >
          <div className="v-rt-dashboard-kpi-icon ready">
            <FaCheckCircle />
          </div>
          <div className="v-rt-dashboard-kpi-data">
            <span className="v-rt-dashboard-kpi-value">{stats.ready}</span>
            <span className="v-rt-dashboard-kpi-label">Ready to Ship</span>
          </div>
        </div>
        <div
          className="v-rt-dashboard-kpi-card v-rt-dashboard-action-card"
          onClick={() => navigate("/retailer/new-order")}
        >
          <div className="v-rt-dashboard-kpi-icon action">
            <FaCamera />
          </div>
          <div className="v-rt-dashboard-kpi-data">
            <span className="v-rt-dashboard-kpi-value action-text">
              New Order
            </span>
            <span className="v-rt-dashboard-kpi-label">Upload Design</span>
          </div>
        </div>
      </div>

      <div className="v-rt-dashboard-main-layout">
        <section className="v-rt-dashboard-ledger-section">
          <div className="v-rt-dashboard-section-header">
            <h3>Orders Ledger</h3>
            <button
              className="v-rt-dashboard-link-btn"
              onClick={() => navigate("/retailer/history")}
            >
              View History <FaChevronRight />
            </button>
          </div>

          <div className="v-rt-dashboard-tabs">
            <button
              className={`v-rt-dashboard-tab ${activeTab === "Active" ? "active" : ""}`}
              onClick={() => setActiveTab("Active")}
            >
              Active Production
            </button>
            <button
              className={`v-rt-dashboard-tab ${activeTab === "Completed" ? "active" : ""}`}
              onClick={() => setActiveTab("Completed")}
            >
              Ready for Pickup
            </button>
            <button
              className={`v-rt-dashboard-tab ${activeTab === "Delivered" ? "active" : ""}`}
              onClick={() => setActiveTab("Delivered")}
            >
              Recent Delivered
            </button>
          </div>

          <div className="v-rt-dashboard-ledger-body">
            {isLoading ? (
              <div className="v-rt-dashboard-empty-state">
                <FaCircleNotch className="v-rt-dashboard-spinner" />
                <p>Syncing live factory data...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="v-rt-dashboard-empty-state">
                <FaBoxOpen className="v-rt-dashboard-empty-icon" />
                <p>No orders found in this status.</p>
              </div>
            ) : (
              <div className="v-rt-dashboard-table-wrapper">
                {/* DESKTOP TABLE */}
                <table className="v-rt-dashboard-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => {
                      const thumb = extractMasterThumbnail(order);
                      const status = normalizeStatus(order.status);
                      return (
                        <tr
                          key={order.id}
                          onClick={() => navigate(`?viewOrder=${order.id}`)}
                        >
                          <td>
                            <div className="v-rt-dashboard-td-product">
                              {thumb ? (
                                <img src={thumb} alt="thumb" />
                              ) : (
                                <div className="v-rt-dashboard-no-thumb">
                                  <FaBoxOpen />
                                </div>
                              )}
                              <span className="v-rt-dashboard-product-name">
                                {order.product || order.orderType}
                              </span>
                            </div>
                          </td>
                          <td className="v-rt-dashboard-td-id">
                            #{order.orderId}
                          </td>
                          <td className="v-rt-dashboard-td-date">
                            {formatDate(order.createdAt)}
                          </td>
                          <td>
                            <span
                              className={`v-rt-dashboard-badge ${getStatusBadge(status)}`}
                            >
                              {status}
                            </span>
                          </td>
                          <td>
                            <FaChevronRight className="v-rt-dashboard-action-icon" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* MOBILE CARDS */}
                <div className="v-rt-dashboard-mobile-list">
                  {filteredOrders.map((order) => {
                    const thumb = extractMasterThumbnail(order);
                    return (
                      <div
                        key={order.id}
                        className="v-rt-dashboard-mobile-card"
                        onClick={() => navigate(`?viewOrder=${order.id}`)}
                      >
                        <div className="v-rt-dashboard-mobile-card-header">
                          <span className="v-rt-dashboard-order-id">
                            #{order.orderId}
                          </span>
                          <span
                            className={`v-rt-dashboard-badge ${getStatusBadge(order.status)}`}
                          >
                            {normalizeStatus(order.status)}
                          </span>
                        </div>
                        <div className="v-rt-dashboard-mobile-card-body">
                          <div className="v-rt-dashboard-mobile-thumb">
                            {thumb ? (
                              <img src={thumb} alt="thumb" />
                            ) : (
                              <div className="v-rt-dashboard-no-thumb">
                                <FaBoxOpen />
                              </div>
                            )}
                          </div>
                          <div className="v-rt-dashboard-mobile-info">
                            <span className="v-rt-dashboard-product-name">
                              {order.product || order.orderType}
                            </span>
                            <span className="v-rt-dashboard-order-date">
                              {formatDate(order.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* RIGHT COLUMN WIDGETS */}
        <aside className="v-rt-dashboard-sidebar-section">
          <div className="v-rt-dashboard-widget v-rt-dashboard-alerts-widget">
            <div className="v-rt-dashboard-widget-header alerts-header">
              <h3>
                <FaBullhorn /> Factory Alerts
              </h3>
            </div>
            <div className="v-rt-dashboard-widget-body">
              {alerts.map((alert) => (
                <div key={alert.id} className="v-rt-dashboard-alert-item">
                  <div className="v-rt-dashboard-alert-icon">
                    <FaExclamationTriangle />
                  </div>
                  <div className="v-rt-dashboard-alert-content">
                    <h4>{alert.title}</h4>
                    <p>{alert.message}</p>
                    <span className="v-rt-dashboard-alert-date">
                      {alert.date}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="v-rt-dashboard-widget v-rt-dashboard-notifications-widget">
            <div className="v-rt-dashboard-widget-header">
              <h3>
                <FaBell /> Notifications
              </h3>
              <span className="v-rt-dashboard-unread-count">2 Unread</span>
            </div>
            <div className="v-rt-dashboard-widget-body">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`v-rt-dashboard-notification-item ${notif.unread ? "unread" : ""}`}
                >
                  <div className="v-rt-dashboard-notif-content">
                    <h4>{notif.title}</h4>
                    <p>{notif.message}</p>
                    <span className="v-rt-dashboard-notif-time">
                      {notif.time}
                    </span>
                  </div>
                  {notif.unread && (
                    <div className="v-rt-dashboard-unread-dot"></div>
                  )}
                </div>
              ))}
              <button className="v-rt-dashboard-view-all-notif">
                Mark all as read
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Conditionally Mount the Order View based on Query Param */}
      {viewOrderId && <ViewRetailerOrder id={viewOrderId} />}
      <Outlet />
    </div>
  );
};

export default RetailerDashboard;
